import { getI18n } from "react-i18next";
import source from "./locales/en/systemFeedback";
import { sharedFeedbackValue, sharedFeedbackText, type SharedRemoteFeedback } from "./sharedFeedbackPresentation";
import { settingsResponse, settingsMessage, type SettingsMessage } from "./settingsManagementPresentation";

type Descriptor = { code: keyof typeof source; params: Record<string, string | number> };
export type SystemRemote = { remote: "system"; fallback: string; status?: number; key?: never; values?: never } & (
 | { kind: "source"; descriptor: Descriptor }
 | { kind: "shared"; value: SharedRemoteFeedback }
 | { kind: "settings"; value: SettingsMessage }
);
const record = (value: unknown): value is Record<string, unknown> => Boolean(value && typeof value === "object" && !Array.isArray(value));
function valid(value: unknown): value is Descriptor {
 if (!record(value) || Object.keys(value).length !== 2 || !Object.prototype.hasOwnProperty.call(value, "code") || !Object.prototype.hasOwnProperty.call(value, "params") || typeof value.code !== "string" || !Object.prototype.hasOwnProperty.call(source, value.code) || !record(value.params)) return false;
 const expected = [...new Set([...source[value.code as keyof typeof source].matchAll(/{{(\w+)}}/g)].map(match => match[1]!))].sort();
 if (JSON.stringify(Object.keys(value.params).sort()) !== JSON.stringify(expected)) return false;
 return Object.values(value.params).every(value => typeof value === "string" ? value.length <= 4096 : typeof value === "number" && Number.isFinite(value));
}
export function isSystemRemote(value: unknown): value is SystemRemote {
 return record(value) && value.remote === "system" && typeof value.fallback === "string";
}
export function systemValue(raw: unknown, descriptor: unknown): SystemRemote | undefined {
 if (typeof raw !== "string") return undefined;
 if (valid(descriptor)) return { remote: "system", kind: "source", fallback: raw, descriptor };
 const shared = sharedFeedbackValue(raw, descriptor);
 if (shared) return { remote: "system", kind: "shared", fallback: raw, value: shared };
 const settings = settingsResponse(descriptor, raw, "pleaseCheckTheFormForErrors");
 return typeof settings === "string" ? undefined : { remote: "system", kind: "settings", fallback: raw, value: settings };
}
export function systemRemote(response: unknown, fallback?: string): SystemRemote | undefined {
 if (!record(response)) return undefined;
 const body = record(response.error) ? response.error : response;
 const raw = typeof body.message === "string" ? body.message : typeof body.error === "string" ? body.error : fallback;
 return systemValue(raw, body.messagePresentation ?? body.errorPresentation ?? body.presentation ?? response.messagePresentation ?? response.presentation);
}
export function systemFeedback(response: unknown, fallback: string): string | SystemRemote {
 return systemRemote(response, fallback) ?? fallback;
}
export function systemResponseText(response: unknown, fallback: string): string {
 const remote = systemRemote(response, fallback);
 return remote ? systemText(remote) : fallback;
}
/** Keep every first-field issue in its original slot; unknown text stays literal. */
export function systemValidationText(response: unknown, fallback: string): string {
 if (!record(response)) return fallback;
 const base = systemResponseText(response, typeof response.message === "string" ? response.message : fallback);
 if (!record(response.errors)) return base;
 const metadata = record(response.fieldErrorPresentation) ? response.fieldErrorPresentation : {};
 const details: string[] = [];
 for (const [field, issues] of Object.entries(response.errors)) {
  if (!Array.isArray(issues) || typeof issues[0] !== "string" || !issues[0].trim()) continue;
  const slots = Object.prototype.hasOwnProperty.call(metadata, field) ? metadata[field] : undefined;
  const value = systemValue(issues[0], Array.isArray(slots) ? slots[0] : undefined);
  details.push(value ? systemText(value) : `${field}: ${issues[0]}`);
 }
 return details.length ? `${base} - ${details.join("; ")}` : base;
}
export function systemText(value: SystemRemote): string {
 const text = systemBaseText(value);
 return value.status === undefined ? text : `${text} (${value.status})`;
}
function systemBaseText(value: SystemRemote): string {
 if (value.kind === "shared") return sharedFeedbackText(value.value);
 if (value.kind === "settings") return settingsMessage(value.value) ?? value.fallback;
 if (!valid(value.descriptor)) return value.fallback;
 const engine = getI18n(); const locale = engine?.resolvedLanguage ?? engine?.language ?? "en";
 const catalog: Record<string, string> | undefined = engine?.isInitialized ? engine.getResourceBundle(locale, "systemFeedback") : undefined;
 const code = value.descriptor.code;
 const template = catalog && Object.prototype.hasOwnProperty.call(catalog, code) && typeof catalog[code] === "string" ? catalog[code]! : source[code];
 return template.replace(/{{(\w+)}}/g, (match, key: string) => Object.prototype.hasOwnProperty.call(value.descriptor.params, key) ? typeof value.descriptor.params[key] === "number" ? new Intl.NumberFormat(locale).format(value.descriptor.params[key]) : String(value.descriptor.params[key]) : match);
}
export class SystemRemoteError extends Error {
 readonly feedback: SystemRemote | undefined;
 constructor(raw: string, response: unknown, status?: number) { super(raw); const feedback = systemRemote(response, raw); this.feedback = feedback && status !== undefined ? { ...feedback, status } : feedback; }
}
export function systemErrorFailure(error: unknown): SystemRemote | undefined {
 return error instanceof SystemRemoteError ? error.feedback : undefined;
}
